import { useCallback, useMemo, useState, type ReactNode } from "react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  Package,
  CheckCircle2,
  Lightbulb,
  GitBranch,
  Target,
  Scale,
  Activity,
  Loader2,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usePageTitle } from "@/hooks/use-page-title";
import { useActivityDetails } from "@/hooks/queries/use-activity-details";
import { useAuthStore } from "@/stores/auth-store";
import { resolveTimeZone } from "@/lib/api/user-activity";
import type {
  FeatureDetail,
  TaskDetail,
  DecisionDetail,
} from "@/lib/api/user-activity-details";
import { Button } from "@/components/ui/button";

/* ── Unified feed item ── */
type FeedItemKind = "feature" | "task" | "decision";

interface FeedItem {
  kind: FeedItemKind;
  id: string;
  ts: string;
  title: string;
  subtitle: string;
  icon: ReactNode;
  color: string;
  href?: string;
}

const PAGE_SIZE = 50;

const decisionCategoryIcon: Record<string, ReactNode> = {
  approach: <Lightbulb className="h-3.5 w-3.5" />,
  architecture: <GitBranch className="h-3.5 w-3.5" />,
  library: <Package className="h-3.5 w-3.5" />,
  scope: <Target className="h-3.5 w-3.5" />,
  tradeoff: <Scale className="h-3.5 w-3.5" />,
};

const kindMeta: Record<FeedItemKind, { label: string; badgeClass: string }> = {
  feature: { label: "Feature", badgeClass: "bg-blue-500/10 text-blue-400" },
  task: { label: "Task", badgeClass: "bg-emerald-500/10 text-emerald-400" },
  decision: { label: "Decision", badgeClass: "bg-yellow-500/10 text-yellow-400" },
};

/* ── Helpers ── */
function toFeatureItems(data: FeatureDetail[]): FeedItem[] {
  return data.map((f) => ({
    kind: "feature" as const,
    id: f.id,
    ts: f.createdAt,
    title: f.title,
    subtitle: `${f.identifier} · ${f.epicName}`,
    icon: <Package className="h-3.5 w-3.5" />,
    color: "text-blue-400/70 border-blue-400/20",
    href: `/features/${f.id}`,
  }));
}

function toTaskItems(data: TaskDetail[]): FeedItem[] {
  return data.map((t) => ({
    kind: "task" as const,
    id: t.id,
    ts: t.completedAt ?? t.createdAt,
    title: t.title,
    subtitle: `${t.identifier} · ${t.featureTitle}`,
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    color: "text-emerald-400/70 border-emerald-400/20",
    href: `/tasks/${t.id}`,
  }));
}

function toDecisionItems(data: DecisionDetail[]): FeedItem[] {
  return data.map((d) => ({
    kind: "decision" as const,
    id: d.id,
    ts: d.createdAt,
    title: d.decision,
    subtitle: `${d.category} · ${d.epicName}`,
    icon: decisionCategoryIcon[d.category] ?? <Lightbulb className="h-3.5 w-3.5" />,
    color: "text-yellow-400/70 border-yellow-400/20",
    href: `/epics/${d.epicId}?tab=activity`,
  }));
}


/* ── Component ── */
export function ActivityPage() {
  usePageTitle("Activity");
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userTimeZone = user?.timeZone;
  const timeZone = resolveTimeZone(userTimeZone);

  const [filter, setFilter] = useState<FeedItemKind | "all">("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Fetch user's own activity only (scope: "self"), use "month" for broadest range
  const sharedParams = { interval: "month" as const, page: 1, scope: "self" as const, timeZone, limit: 200 };

  const featuresQ = useActivityDetails({ ...sharedParams, metricType: "features", enabled: true });
  const tasksQ = useActivityDetails({ ...sharedParams, metricType: "tasks", enabled: true });
  const decisionsQ = useActivityDetails({ ...sharedParams, metricType: "decisions", enabled: true });

  const isLoading = featuresQ.isLoading || tasksQ.isLoading || decisionsQ.isLoading;
  const isError = featuresQ.isError || tasksQ.isError || decisionsQ.isError;

  const items = useMemo(() => {
    const features = (featuresQ.data?.data ?? []) as FeatureDetail[];
    const tasks = (tasksQ.data?.data ?? []) as TaskDetail[];
    const decisions = (decisionsQ.data?.data ?? []) as DecisionDetail[];

    // Trust the API's scope=self filtering — no client-side double-filtering needed
    const all: FeedItem[] = [
      ...toFeatureItems(features),
      ...toTaskItems(tasks),
      ...toDecisionItems(decisions),
    ];
    all.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
    return all;
  }, [featuresQ.data, tasksQ.data, decisionsQ.data]);

  const filtered = filter === "all" ? items : items.filter((i) => i.kind === filter);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  }, []);

  // Reset visible count when filter changes
  const handleFilterChange = useCallback((key: FeedItemKind | "all") => {
    setFilter(key);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const counts = useMemo(() => {
    const c = { feature: 0, task: 0, decision: 0 };
    for (const i of items) if (i.kind in c) c[i.kind as keyof typeof c]++;
    return c;
  }, [items]);

  return (
    <div className="pt-4">
      <div className="glass-card rounded-2xl p-4 md:p-6 space-y-3">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold">Your Activity</h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          Your recent features, tasks, and decisions
        </p>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {([
          { key: "all", label: "All", count: items.length },
          { key: "feature", label: "Features", count: counts.feature },
          { key: "task", label: "Tasks", count: counts.task },
          { key: "decision", label: "Decisions", count: counts.decision },
        ] as const).map((f) => (
          <button
            key={f.key}
            onClick={() => { handleFilterChange(f.key); }}
            className={cn(
              "flex items-center gap-1.5 rounded-xl px-3.5 min-h-[44px] text-[13px] font-medium transition-colors whitespace-nowrap",
              filter === f.key
                ? "bg-white/[0.08] text-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]"
            )}
          >
            {f.label}
            {f.count > 0 && (
              <span className={cn(
                "flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-semibold",
                filter === f.key ? "bg-white/[0.1] text-foreground" : "bg-white/[0.04] text-muted-foreground"
              )}>
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Feed */}
      <div className="rounded-2xl overflow-hidden border border-white/[0.06]">
        {isError ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/[0.06]">
              <Activity className="h-7 w-7 text-red-400/60" />
            </div>
            <p className="text-[15px] font-medium text-foreground">Unable to load activity</p>
            <p className="mt-1.5 max-w-[260px] text-center text-[13px] text-muted-foreground leading-relaxed">
              Something went wrong loading your activity. Please try again later.
            </p>
            <Button
              variant="ghost"
              className="mt-4 text-xs text-muted-foreground"
              onClick={() => {
                void featuresQ.refetch();
                void tasksQ.refetch();
                void decisionsQ.refetch();
              }}
            >
              Retry
            </Button>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground/60" />
            <p className="mt-3 text-[13px] text-muted-foreground">Loading your activity…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/[0.04]">
              <Activity className="h-7 w-7 text-muted-foreground/60" />
            </div>
            <p className="text-[15px] font-medium text-foreground">No activity yet</p>
            <p className="mt-1.5 max-w-[260px] text-center text-[13px] text-muted-foreground leading-relaxed">
              {filter === "all"
                ? "Your features, tasks, decisions, and AI sessions will appear here."
                : `No ${filter}s found yet.`}
            </p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-white/[0.04]">
              {visible.map((item) => (
                <button
                  key={`${item.kind}:${item.id}`}
                  onClick={() => { if (item.href) void navigate(item.href); }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 min-h-[52px] text-left hover:bg-white/[0.03] active:bg-white/[0.05] transition-colors"
                >
                  {/* Icon */}
                  <div className={cn(
                    "shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-white/[0.04] border",
                    item.color
                  )}>
                    {item.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium text-foreground leading-snug line-clamp-2">
                      {item.title}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={cn("text-[11px] font-semibold px-1.5 py-0.5 rounded shrink-0", kindMeta[item.kind].badgeClass)}>
                        {kindMeta[item.kind].label}
                      </span>
                      <span className="text-[12px] text-muted-foreground truncate">{item.subtitle}</span>
                    </div>
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                      {(() => { try { return formatDistanceToNow(new Date(item.ts), { addSuffix: true }); } catch { return ""; } })()}
                    </p>
                  </div>

                  {/* Drill-down chevron */}
                  {item.href && (
                    <ChevronRight className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                  )}
                </button>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="border-t border-white/[0.04] px-4 py-3">
                <Button
                  variant="ghost"
                  className="w-full text-xs text-muted-foreground hover:text-foreground"
                  onClick={handleLoadMore}
                >
                  Load more ({filtered.length - visibleCount} remaining)
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
}
